import React, { useState } from 'react';
import DatePicker from 'react-datepicker';

import FilterLogs from '../../../entities/filter/logs';
import { SimpleObject } from '../../../entities/simple-object';
import { fromJsDate } from '../../../services/formatter/date';
import { strings } from '../../../localization/strings';
import { useDebounce } from '../../../hooks/use-debounce';
import { createDateKeyDownHandler } from '../../../utils/date-input-handler';

export default function LogsFilter({
  onUpdate,
  filterState,
}: {
  onUpdate: (filters: FilterLogs) => void;
  filterState: FilterLogs;
}) {
  const [dateValue, setDateValue] = useState<SimpleObject<Date | null>>({
    created_at_from: filterState.created_at_from instanceof Date ? filterState.created_at_from : null,
    created_at_to: filterState.created_at_to instanceof Date ? filterState.created_at_to : null,
  });

  const [inputValue, setInputValue] = useState({
    user: filterState.user || '',
    ip: filterState.ip || '',
  });

  const onDelayedTextChange = useDebounce((name: keyof FilterLogs, value: string) => {
    onUpdate({ ...filterState, [name]: value });
  }, 500);

  const onTextChange = (name: keyof FilterLogs, value: string) => {
    setInputValue({ ...inputValue, [name]: value });
    onDelayedTextChange(name, value);
  };

  const onChangeDate = (date: [Date | null, Date | null]) => {
    setDateValue({ created_at_from: date[0], created_at_to: date[1] });

    onUpdate({
      ...filterState,
      created_at_from: date[0] ? fromJsDate(date[0]) : null,
      created_at_to: date[1] ? fromJsDate(date[1]) : null,
    });
  };

  const clearFilter = () => {
    setDateValue({ created_at_from: null, created_at_to: null });
    setInputValue({ user: '', ip: '' });
    onUpdate({ page: 0, sort: filterState.sort || '-id' });
  };

  return (
    <div className="bg-white py-6">
      <div className="grid max-w-7xl grid-cols-1 gap-6 px-4 text-sm sm:px-6 lg:grid-cols-4 lg:px-8">
        <fieldset>
          <legend className="block font-medium">{strings.logs.filters.dateRange}</legend>
          <div className="mt-4">
            <DatePicker
              monthsShown={2}
              showYearDropdown
              dateFormat="yyyy-MM-dd"
              isClearable
              className="block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              selectsRange
              startDate={dateValue.created_at_from}
              selected={dateValue.created_at_from}
              endDate={dateValue.created_at_to}
              onChange={onChangeDate}
              onKeyDown={createDateKeyDownHandler(
                'created_at_from',
                'created_at_to',
                dateValue,
                filterState,
                setDateValue,
                onUpdate,
              )}
            />
          </div>
        </fieldset>
        <fieldset>
          <legend className="block font-medium">{strings.logs.filters.user}</legend>
          <div className="mt-4">
            <input
              type="search"
              value={inputValue.user}
              onChange={(event) => onTextChange('user', event.target.value)}
              placeholder={strings.logs.filters.userPlaceholder}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
        </fieldset>
        <fieldset>
          <legend className="block font-medium">{strings.logs.filters.ip}</legend>
          <div className="mt-4">
            <input
              type="search"
              value={inputValue.ip}
              onChange={(event) => onTextChange('ip', event.target.value)}
              placeholder={strings.logs.filters.ipPlaceholder}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
        </fieldset>
        <div className="flex items-end">
          <button
            type="button"
            className="rounded-md px-3 py-2 text-sm font-medium text-gray-500 hover:text-gray-700"
            onClick={clearFilter}
          >
            {strings.filter.clearFilter}
          </button>
        </div>
      </div>
    </div>
  );
}
